import React from "react";
import { AbsoluteFill, Audio, Img, Sequence, interpolate, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { fadeIn, slideUp, scaleIn, progressBar } from "../utils/animations";

export interface ProductFeature {
  icon: string;
  title: string;
  desc: string;
}

export interface ProductVideoProps {
  productName: string;
  tagline: string;
  features: ProductFeature[];
  price?: string;
  priceLabel?: string;
  ctaText: string;
  accentColor?: string;
  bgColor?: string;
  heroImageUrl?: string;
  voiceFile?: string;
}

// ── Hero Scene ────────────────────────────────────────────────────────────────

const HeroScene: React.FC<{ props: ProductVideoProps }> = ({ props: p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = p.accentColor ?? "#38bdf8";

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>
      {p.heroImageUrl && (
        <Img
          src={p.heroImageUrl.startsWith("http") ? p.heroImageUrl : staticFile(p.heroImageUrl)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.35 }}
        />
      )}
      {/* Gradient overlay */}
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, ${p.bgColor ?? "#020617"}cc 0%, ${p.bgColor ?? "#020617"}ee 100%)` }} />
      {/* Top bar */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent, boxShadow: `0 0 24px ${accent}` }} />
      {/* Glow */}
      <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: `radial-gradient(ellipse, ${accent}22 0%, transparent 70%)` }} />

      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60, gap: 24 }}>
        {/* Brand pill */}
        <div style={{ opacity: fadeIn(frame, 5), background: `${accent}22`, border: `1px solid ${accent}55`, borderRadius: 40, padding: "8px 28px", fontSize: isVertical ? 26 : 20, color: accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
          product
        </div>
        {/* Product name */}
        <div style={{ opacity: fadeIn(frame, 12), transform: `translateY(${slideUp(frame, 12)}px)`, fontSize: isVertical ? 96 : 72, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.05, textShadow: `0 0 60px ${accent}44` }}>
          {p.productName}
        </div>
        {/* Tagline */}
        <div style={{ opacity: fadeIn(frame, 26), transform: `translateY(${slideUp(frame, 26, 18, 30)}px)`, fontSize: isVertical ? 44 : 32, color: "#94a3b8", textAlign: "center", lineHeight: 1.5, maxWidth: "80%" }}>
          {p.tagline}
        </div>
        {/* Accent line */}
        <div style={{ opacity: fadeIn(frame, 38), width: interpolate(frame, [38, 58], [0, 120], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }), height: 3, background: accent, borderRadius: 2, boxShadow: `0 0 12px ${accent}` }} />
      </AbsoluteFill>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── Features Scene ────────────────────────────────────────────────────────────

const FeaturesScene: React.FC<{ props: ProductVideoProps }> = ({ props: p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = p.accentColor ?? "#38bdf8";

  return (
    <AbsoluteFill style={{ background: p.bgColor ?? "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: `linear-gradient(${accent}44 1px, transparent 1px), linear-gradient(90deg, ${accent}44 1px, transparent 1px)`, backgroundSize: "60px 60px" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent }} />

      <AbsoluteFill style={{ padding: isVertical ? "100px 60px 60px" : "80px 80px 60px", flexDirection: "column", justifyContent: "center" }}>
        {/* Section label */}
        <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 26 : 20, color: accent, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 28 }}>
          key features
        </div>
        {/* Feature rows */}
        {p.features.slice(0, 3).map((f, i) => (
          <div key={i} style={{
            opacity: fadeIn(frame, 15 + i * 20),
            transform: `translateX(${interpolate(frame, [15 + i * 20, 33 + i * 20], [-60, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })}px)`,
            display: "flex", alignItems: "flex-start", gap: 28,
            background: `${accent}0a`, border: `1px solid ${accent}22`, borderRadius: 16,
            padding: isVertical ? "28px 32px" : "20px 28px", marginBottom: 20,
          }}>
            <div style={{ fontSize: isVertical ? 52 : 40, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{f.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: isVertical ? 40 : 28, fontWeight: 800, color: "#f1f5f9", marginBottom: 6 }}>{f.title}</div>
              <div style={{ fontSize: isVertical ? 30 : 22, color: "#64748b", lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </AbsoluteFill>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 165)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── CTA Scene ─────────────────────────────────────────────────────────────────

const CtaScene: React.FC<{ props: ProductVideoProps }> = ({ props: p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const accent = p.accentColor ?? "#38bdf8";

  return (
    <AbsoluteFill style={{ background: p.bgColor ?? "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: accent, boxShadow: `0 0 20px ${accent}` }} />
      <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, background: `radial-gradient(ellipse, ${accent}15 0%, transparent 65%)` }} />

      {p.price && (
        <div style={{ opacity: fadeIn(frame, 8), transform: `scale(${scaleIn(frame, 8)})`, textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: isVertical ? 110 : 80, fontWeight: 900, color: accent, lineHeight: 1 }}>{p.price}</div>
          {p.priceLabel && <div style={{ fontSize: isVertical ? 30 : 22, color: "#64748b", marginTop: 8 }}>{p.priceLabel}</div>}
        </div>
      )}

      <div style={{ opacity: fadeIn(frame, 20), transform: `translateY(${slideUp(frame, 20)}px)`, fontSize: isVertical ? 72 : 52, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.15, marginBottom: 32 }}>
        {p.ctaText}
      </div>

      <div style={{ opacity: fadeIn(frame, 34), transform: `scale(${scaleIn(frame, 34)})`, background: accent, color: "#020617", padding: isVertical ? "22px 56px" : "16px 44px", borderRadius: 50, fontSize: isVertical ? 38 : 28, fontWeight: 900, letterSpacing: 1, boxShadow: `0 0 40px ${accent}66` }}>
        Get Started →
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: accent }} />
      </div>
    </AbsoluteFill>
  );
};

// ── ProductVideo ──────────────────────────────────────────────────────────────

export const ProductVideo: React.FC<ProductVideoProps> = (props) => {
  return (
    <AbsoluteFill>
      <Sequence from={0} durationInFrames={90}><HeroScene props={props} /></Sequence>
      <Sequence from={90} durationInFrames={165}><FeaturesScene props={props} /></Sequence>
      <Sequence from={255} durationInFrames={90}><CtaScene props={props} /></Sequence>
      {props.voiceFile && <Audio src={staticFile(props.voiceFile)} />}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
